// Budgets on a task follow the task.
//
// A role's budget on a task (`raciOnTasks`) can repeat per day, week or month over its own
// window inside the task (`startDate` - `endDate`). The repeat only decides how the hours
// are counted over that window (mf-utils#getHoursNeedFromRaci), so the one thing to keep
// right when the task's dates change is the window: it moves when the task moves and never
// reaches outside the task. Cost lines tied to a task do the same in
// utils/mission.js#reconcileCostItems.

import { addBusinessDays, addDays, addMonths, differenceInBusinessDays, differenceInCalendarMonths } from "date-fns"
import { endOfDay, isAfter, isBefore, isLastDayOfMonth, isSameDay, isWeekend, lastDayOfMonth, startOfDay } from "date-fns"
import { toZonedTime } from "date-fns-tz"

import { getTimeUtc } from "./dates"

const day = (ms) => startOfDay(ms instanceof Date ? ms : toZonedTime(ms))

const nextWorkingDay = (date) => {
    let d = date
    while (isWeekend(d)) d = addDays(d, 1)
    return d
}

/**
 * Which tasks' dates a set of changes moves.
 *
 * @param changes {Array<{obj, change|changes}>} As passed to app.actionItemUpdateBulk
 * @param taskById {object} The tasks as they are now, by id - what "before" is read from
 * @return {Array<{ taskId, before: {startDate, endDate}, after: {startDate, endDate} }>}
 */
export const getTaskMoves = (changes = [], taskById = {}) =>
    changes
        .map((entry) => {
            const change = entry?.change || entry?.changes
            const before = taskById[change?._id || entry?.obj?._id]

            if (!change || !before?.startDate || !before?.endDate) return null

            const after = {
                startDate: change.startDate ?? before.startDate,
                endDate: change.endDate ?? before.endDate,
            }

            if (!after.startDate || !after.endDate) return null
            if (isSameDay(day(after.startDate), day(before.startDate)) && isSameDay(day(after.endDate), day(before.endDate))) {
                return null
            }

            return {
                taskId: before._id,
                before: { startDate: before.startDate, endDate: before.endDate },
                after,
            }
        })
        .filter(Boolean)

/**
 * Where a budget's window sits after its task's dates changed. Null when it stays as it is -
 * a budget without a window of its own spans the task and needs nothing.
 *
 * - The task moved (both of its dates went the same way): the window keeps its place in
 *   the task - the same working days from the task's start, the same length. A monthly
 *   budget moves by whole months.
 * - Then it is kept inside the task. A window the task no longer reaches at all shrinks to
 *   the task's nearest day.
 *
 * @param entry {object} The raciOnTasks entry {startDate, endDate, frequency}
 * @param before {{ startDate, endDate }} The task's dates before the change
 * @param after {{ startDate, endDate }} The task's dates now
 * @return {{ startDate: Number, endDate: Number }|null}
 */
export const getBudgetWindow = (entry = {}, before = {}, after = {}) => {
    if (!entry.startDate || !entry.endDate) return null
    if (!before.startDate || !before.endDate || !after.startDate || !after.endDate) return null

    const taskStart = day(after.startDate)
    const taskEnd = day(after.endDate)
    const oldStart = day(before.startDate)
    const oldEnd = day(before.endDate)

    if (isAfter(taskStart, taskEnd)) return null

    const later = (a, b) => (isAfter(a, b) ? a : b)
    const earlier = (a, b) => (isBefore(a, b) ? a : b)

    // Where it was, inside the task as it was
    let start = later(day(entry.startDate), oldStart)
    let end = earlier(day(entry.endDate), oldEnd)

    if (isAfter(start, end)) end = start

    const startWent = Math.sign(taskStart - oldStart)
    const endWent = Math.sign(taskEnd - oldEnd)

    if (startWent && startWent === endWent) {
        if (entry.frequency === "Per month") {
            const months = differenceInCalendarMonths(taskStart, oldStart)
            const movedEnd = addMonths(end, months)

            end = isLastDayOfMonth(end) ? lastDayOfMonth(movedEnd) : movedEnd
            start = addMonths(start, months)
        } else {
            const offset = differenceInBusinessDays(nextWorkingDay(start), nextWorkingDay(oldStart))
            const length = Math.max(0, differenceInBusinessDays(end, nextWorkingDay(start)))

            start = addBusinessDays(nextWorkingDay(taskStart), offset)
            end = addBusinessDays(start, length)
        }
    }

    if (isAfter(start, taskEnd)) {
        start = taskEnd
        end = taskEnd
    } else if (isBefore(end, taskStart)) {
        start = taskStart
        end = taskStart
    } else {
        start = later(start, taskStart)
        end = earlier(end, taskEnd)
    }

    if (isSameDay(start, day(entry.startDate)) && isSameDay(end, day(entry.endDate))) return null

    return { startDate: getTimeUtc(startOfDay(start)), endDate: getTimeUtc(endOfDay(end)) }
}
